import React, { createContext, useContext, useState, useEffect } from 'react';

const FirmConfigContext = createContext(null);

// Default Abundo Wealth branding
const DEFAULT_CONFIG = {
  firm_name: 'Abundo Wealth',
  subdomain: null,
  logo_url: null,
  primary_color: '#1a365d',
  secondary_color: '#2b6cb0',
};

/**
 * Extract subdomain from current hostname.
 * Returns null for root domains and managed preview hosts.
 */
const getSubdomain = () => {
  const hostname = window.location.hostname;
  // App Platform's generated app name is not a registered firm subdomain.
  if (hostname.endsWith('.ondigitalocean.app')) return null;
  const parts = hostname.split('.');
  // Need at least 3 parts for a subdomain (acme.abundowealth.com)
  if (parts.length >= 3 && !['www', 'api', 'app'].includes(parts[0])) {
    return parts[0].toLowerCase();
  }
  return null;
};

export const FirmConfigProvider = ({ children }) => {
  const [firmConfig, setFirmConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const subdomain = getSubdomain();
    if (!subdomain) {
      setLoading(false);
      return;
    }

    const fetchConfig = async () => {
      try {
        const baseUrl = process.env.REACT_APP_API_URL || '';
        const res = await fetch(`${baseUrl}/api/v1/admin/firm-config/${encodeURIComponent(subdomain)}`);
        if (res.ok) {
          const data = await res.json();
          setFirmConfig({ ...DEFAULT_CONFIG, ...data });
          // Apply CSS custom properties for theming
          const root = document.documentElement;
          root.style.setProperty('--firm-primary-color', data.primary_color || DEFAULT_CONFIG.primary_color);
          root.style.setProperty('--firm-secondary-color', data.secondary_color || DEFAULT_CONFIG.secondary_color);
        }
      } catch (err) {
        console.error('[FirmConfig] Failed to load firm config:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  return (
    <FirmConfigContext.Provider value={{ firmConfig, loading }}>
      {children}
    </FirmConfigContext.Provider>
  );
};

/**
 * Hook to access firm configuration (branding, name, colors).
 * Returns default config when used outside provider or on root domain.
 */
export const useFirmConfig = () => {
  const context = useContext(FirmConfigContext);
  if (!context) {
    return { firmConfig: DEFAULT_CONFIG, loading: false };
  }
  return context;
};

export default FirmConfigContext;
